import { useCallback, useEffect, useRef, useState } from 'react';
import type { ScreenPlaylist, Slide } from '@up2d8/contracts';
import { SIGNAGE_REFRESH_SECONDS } from '@up2d8/contracts';
import { formatTime } from '../../shared/format';
import { SafeHtml } from '../../shared/safe-html';

/**
 * De weergave op een digitaal scherm (Stap 41).
 *
 * Dit is de enige pagina in het platform zonder sessie, zonder navigatie en zonder knoppen — en
 * bijna zonder componenten uit `packages/ui`, want die zijn gemaakt voor een telefoon op een halve
 * meter en dit hangt op vier meter in een kantine. De tekst is daarom veel groter dan de
 * grootste maat uit de style guide. Dat is een bewuste afwijking, genoteerd in
 * `docs/design/deviations.md`; de **kleuren** komen wel uit de huisstijl van de klant.
 *
 * Drie dingen maken het een scherm en geen webpagina:
 *
 * 1. **Het onthoudt zijn token** in `localStorage`. Eén keer koppelen met een code die een
 *    beheerder voorleest, daarna start het altijd in de weergave — ook na een stroomstoring.
 * 2. **Het bewaart zijn laatste playlist**, ook in `localStorage`. Bij netwerkverlies draait het
 *    door met wat het had (acceptatiecriterium 3): een leeg scherm in een kantine is erger dan een
 *    scherm met het bericht van gisteren.
 * 3. **Het haalt elke minuut opnieuw op.** Zo verschijnt een nieuw bericht binnen een minuut en
 *    verdwijnt een ingetrokken bericht even snel, zonder dat er iets naar het scherm geduwd moet
 *    worden — een scherm in een kantine heeft geen verbinding die je open kunt houden.
 */

const TOKEN_KEY = 'up2d8.signage.token';
const CACHE_KEY = 'up2d8.signage.playlist';

export function ScreenDisplay() {
  const [token, setToken] = useState<string | null>(() => read(TOKEN_KEY));
  const [playlist, setPlaylist] = useState<ScreenPlaylist | null>(() => readJson(CACHE_KEY));
  const [offline, setOffline] = useState(false);

  const fetchPlaylist = useCallback(async (current: string) => {
    try {
      const response = await fetch(
        `/api/v1/signage/screens/playlist?token=${encodeURIComponent(current)}`,
      );
      if (response.status === 401) {
        // Het token is ongeldig geworden (het scherm is opnieuw gekoppeld): terug naar de
        // koppelpagina, want doorgaan met een dode sleutel levert alleen een stil scherm op.
        window.localStorage.removeItem(TOKEN_KEY);
        setToken(null);
        return;
      }
      if (!response.ok) throw new Error(`server gaf ${response.status}`);

      const body = (await response.json()) as ScreenPlaylist;
      setPlaylist(body);
      setOffline(false);
      write(CACHE_KEY, JSON.stringify(body));
    } catch {
      // Geen netwerk: de laatste playlist blijft staan. Alleen het klokje in de hoek verraadt
      // dat de inhoud oud is.
      setOffline(true);
    }
  }, []);

  useEffect(() => {
    if (!token) return;
    void fetchPlaylist(token);
    const timer = window.setInterval(
      () => void fetchPlaylist(token),
      SIGNAGE_REFRESH_SECONDS * 1000,
    );
    return () => window.clearInterval(timer);
  }, [token, fetchPlaylist]);

  if (!token) {
    return (
      <PairingScreen
        onPaired={(newToken) => {
          write(TOKEN_KEY, newToken);
          setToken(newToken);
        }}
      />
    );
  }

  return <Rotation playlist={playlist} offline={offline} />;
}

/**
 * Het koppelscherm: twee velden en een knop.
 *
 * Grote velden, want dit wordt getypt met een afstandsbediening of een toetsenbord op een ladder.
 */
function PairingScreen({ onPaired }: { onPaired: (token: string) => void }) {
  const [tenantCode, setTenantCode] = useState('');
  const [pairingCode, setPairingCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const response = await fetch('/api/v1/signage/screens/register', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ tenantCode: tenantCode.trim(), pairingCode: pairingCode.trim() }),
      });
      if (!response.ok) {
        setError('Deze koppelcode hoort bij geen enkel scherm.');
        return;
      }
      const body = (await response.json()) as { token: string };
      onPaired(body.token);
    } catch {
      setError('Geen verbinding met de server.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-section">
      <div className="w-full max-w-xl rounded-lg bg-surface p-section shadow-sm">
        <h1 className="font-heading text-page font-semibold text-text-primary">Scherm koppelen</h1>
        <p className="mt-element text-content text-text-secondary">
          Vraag de beheerder om de bedrijfscode en de koppelcode van dit scherm.
        </p>

        <label className="mt-section block font-heading text-content font-medium text-text-secondary">
          Bedrijfscode
          <input
            className="mt-2 h-14 w-full rounded-md border border-border bg-surface px-element text-title-md text-text-primary"
            value={tenantCode}
            autoCapitalize="none"
            onChange={(event) => setTenantCode(event.target.value)}
            data-testid="signage-tenant-code"
          />
        </label>

        <label className="mt-element block font-heading text-content font-medium text-text-secondary">
          Koppelcode
          <input
            className="mt-2 h-14 w-full rounded-md border border-border bg-surface px-element text-title-md uppercase text-text-primary"
            value={pairingCode}
            autoCapitalize="characters"
            onChange={(event) => setPairingCode(event.target.value)}
            data-testid="signage-pairing-code"
          />
        </label>

        {error ? (
          <p role="alert" className="mt-element text-content text-danger">
            {error}
          </p>
        ) : null}

        <button
          type="button"
          className="mt-section h-14 w-full rounded-pill bg-brand-primary px-element text-title-md font-semibold text-text-on-dark disabled:opacity-50"
          disabled={busy || tenantCode.trim() === '' || pairingCode.trim() === ''}
          onClick={() => void submit()}
          data-testid="signage-pair"
        >
          Koppelen
        </button>
      </div>
    </div>
  );
}

/**
 * De rotatie zelf.
 *
 * Eén dia tegelijk, met de duur die de beheerder per dia koos. De teller loopt door op de
 * **huidige** reeks: komt er een nieuwe playlist binnen, dan begint hij bij de eerste dia — dat is
 * eerlijker dan middenin een reeks blijven hangen die niet meer bestaat.
 */
function Rotation({ playlist, offline }: { playlist: ScreenPlaylist | null; offline: boolean }) {
  const [index, setIndex] = useState(0);
  const slides = playlist?.slides ?? [];
  const fingerprint = slides.map((slide) => slide.id).join('|');
  const timing = slides.map((slide) => `${slide.id}:${slide.durationSeconds}`).join('|');
  const previous = useRef(fingerprint);

  if (previous.current !== fingerprint) {
    previous.current = fingerprint;
    if (index !== 0) setIndex(0);
  }

  useEffect(() => {
    if (slides.length === 0) return;
    const current = slides[index % slides.length] as Slide;
    const timer = window.setTimeout(
      () => setIndex((value) => (value + 1) % slides.length),
      Math.max(3, current.durationSeconds) * 1000,
    );
    return () => window.clearTimeout(timer);
    // Op de inhoud van de reeks en niet op de array: elke verversing (elke minuut) geeft een nieuwe
    // array, en dan begon de timer van de huidige dia opnieuw - een dia van 60 s of langer bleef
    // zo voor altijd staan.
  }, [index, timing]);

  // De kleur van de klant als achtergrond van de balk: een scherm in de gang is van hén.
  const brand = playlist?.brandColor ?? undefined;

  if (!playlist) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <p className="text-page text-text-secondary">Bezig met laden…</p>
      </div>
    );
  }

  const slide = slides.length > 0 ? (slides[index % slides.length] as Slide) : null;

  return (
    <div className="flex min-h-screen flex-col bg-background" data-testid="signage-display">
      <header
        className="flex items-center justify-between gap-element px-section py-element"
        style={{ backgroundColor: brand }}
      >
        <div className="flex items-center gap-element">
          {playlist.logoUrl ? <img src={playlist.logoUrl} alt="" className="h-12 w-auto" /> : null}
          <span className="font-heading text-hero font-bold text-text-on-dark">
            {playlist.tenantName}
          </span>
        </div>
        <span className="text-content text-text-on-dark opacity-80">{playlist.screenName}</span>
      </header>

      <main className="flex flex-1 items-center justify-center p-section">
        {!slide ? (
          <p className="text-page text-text-secondary" data-testid="signage-empty">
            Er staat nog niets op dit scherm.
          </p>
        ) : slide.type === 'media' && slide.imageUrl ? (
          // Een losse afbeelding: zo groot mogelijk, zonder uitsnede. Een poster die half
          // afgesneden is, is onleesbaar vanaf vier meter.
          <img
            src={slide.imageUrl}
            alt=""
            className="max-h-full max-w-full object-contain"
            data-testid="signage-slide"
          />
        ) : (
          <article className="mx-auto max-w-5xl" data-testid="signage-slide">
            {slide.imageUrl ? (
              <img
                src={slide.imageUrl}
                alt=""
                className="mb-section max-h-[40vh] w-full object-contain"
              />
            ) : null}
            <h1 className="font-heading text-[4vw] font-bold leading-tight text-text-primary">
              {slide.title}
            </h1>
            {/*
              De tekst komt van de server en is daar opgeschoond (`cleanForScreen`): scripts en
              gebeurtenissen zijn eruit. `SafeHtml` schoont ze in de browser nog eens op — en de
              opmaak van een bericht is precies wat het leesbaar maakt.
            */}
            <SafeHtml
              className="signage-body mt-element text-[2vw] leading-snug text-text-secondary"
              html={slide.bodyHtml}
            />
          </article>
        )}
      </main>

      <footer className="flex items-center justify-between px-section py-2 text-small text-text-muted">
        <span>{slides.length > 0 ? `${(index % slides.length) + 1} / ${slides.length}` : ''}</span>
        {/*
          Het enige dat verraadt dat de inhoud oud is. Niet een waarschuwing over het hele scherm:
          een kantine hoort geen foutmelding te tonen omdat het netwerk even wegviel.
        */}
        <span>
          {offline ? 'offline — laatste inhoud van ' : ''}
          {formatTime(playlist.generatedAt)}
        </span>
      </footer>
    </div>
  );
}

function read(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    // Een kiosk-browser met opslag uit: koppelen lukt dan elke start opnieuw, en dat is beter dan
    // een pagina die niets doet.
    return null;
  }
}

function readJson<T>(key: string): T | null {
  const raw = read(key);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

function write(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Zie `read`: zonder opslag werkt het scherm, maar het vergeet zijn token bij een herstart.
  }
}
